import React, { useEffect } from "react";

// region External libraries
import { matchesSubstringIgnoreCaseAndAccents } from "@npm-telluria-tecnologia/telluria-utils";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { useAtom } from "jotai";
import { useMsal } from "@azure/msal-react";
// endregion External libraries
// region Helpers
import { groupUsersByRoleId, IRoleWithUsers, sortAlphabetically } from "src/helpers/utils";
// endregion Helpers
// region Languages
import { FilterMessages, GlobalMessages, UserRegisterMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Atoms
import SearchField from "@atoms/SearchField";
import UserProfile from "@atoms/UserProfile";
import Loading from "@atoms/Loading";
import { selectedModuleIdAtom } from "@atoms/SelectIdentityModule";
// endregion Atoms
// region Molecules
import RoleCard from "src/molecules/RoleCard";
// endregion Molecules
// region Organisms
// endregion Organisms
// region Pages
// endregion Pages
// region Store
import { TUserAllWithPaginationQueries, userAllWithPagination } from "src/store/graphql/groups/user/query.graphql";
import {
  ECommandResultStatus,
  EWhereClausesOperators,
  RoleQueriesGetAllWithPaginationArgs,
  RoleType,
  UserQueriesGetAllWithPaginationArgs,
  UserType
} from "src/store/graphql/helpers/types.graphql";
import { roleAllWithPagination, TRoleAllWithPaginationQueries } from "src/store/graphql/groups/role/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IRoleSelectionListProps {
  currentRoleId?: string;
  onRoleChange: (roleId: string) => void;
  isSelectedStep: boolean;
  onSelectedStepChange: () => void;
}

const GroupSelectionList: React.FC<IRoleSelectionListProps> = ({ currentRoleId, isSelectedStep, onRoleChange, onSelectedStepChange }) => {
  // region queries
  const [QueryGetUserAllWithPagination] = useLazyQuery<TUserAllWithPaginationQueries,
    UserQueriesGetAllWithPaginationArgs>(
      userAllWithPagination([
        "id", "name", "imageUrl", "admin", "userRoles.roleId", "userRoles.role.moduleId"
      ])
    );

  const [QueryGetRoleAllWithPagination] = useLazyQuery<TRoleAllWithPaginationQueries,
    RoleQueriesGetAllWithPaginationArgs>(
      roleAllWithPagination([
        "id", "name"
      ])
    );
  // endregion queries
  // region hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { instance } = useMsal();
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  // endregion hooks

  // region states
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [roles, setRoles] = React.useState<IRoleWithUsers[]>([]);
  const [displayedRoles, setDisplayedRoles] = React.useState<IRoleWithUsers[]>([]);
  // endregion states

  // region functions
  const orderRoles = (roles: IRoleWithUsers[]) => moveCurrentUserRoleToFront(sortAlphabetically(roles, "name"));

  const moveCurrentUserRoleToFront = (roles: IRoleWithUsers[]) => {
    const userGroupIndex = roles.findIndex((role) => role.id === currentRoleId);

    if (userGroupIndex === -1) {
      return roles;
    }

    const userGroupData = roles[userGroupIndex];

    roles.splice(userGroupIndex, 1);
    roles.unshift(userGroupData);

    return roles;
  };

  const filterDataOnScreen = (termForFilteringDataOnScreen: string) => {
    if (!termForFilteringDataOnScreen) {
      setDisplayedRoles(orderRoles(roles));

      return;
    }

    const match = (propValue: string) => {
      const formattedPropValue = propValue ?? "";

      return matchesSubstringIgnoreCaseAndAccents(formattedPropValue.toString(), termForFilteringDataOnScreen);
    };

    const rolesClone = _.cloneDeep(roles);

    const filteredRoles = rolesClone.filter((role) => (match(role.name)));

    setDisplayedRoles(filteredRoles);
  };

  const getRoles = async () => {
    try {
      const query = await QueryGetRoleAllWithPagination({
        variables: {
          where: [
            {
              property: "moduleId",
              operator: EWhereClausesOperators.EQUALS,
              value: selectedModuleId
            }
          ]
        }
      });

      const response = query.data?.role.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as RoleType[];

      return response.result as RoleType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as RoleType[];
    }
  };

  const getUsers = async () => {
    try {
      const query = await QueryGetUserAllWithPagination();

      const response = query.data?.user.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as UserType[];

      return response.result as UserType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as UserType[];
    }
  };
  // endregion functions

  useEffect(() => {
    isSelectedStep && onSelectedStepChange();
  }, [isSelectedStep]);

  useEffect(() => {
    (async () => {
      if (!selectedModuleId) return;

      setIsLoading(true);

      const users = await getUsers();
      const roles = await getRoles();

      const isCurrentUserAdmin = instance.getActiveAccount()?.idTokenClaims?.extension_Admin;

      const groupedUsers = groupUsersByRoleId(
        isCurrentUserAdmin ? users : users.filter((user) => !user.admin),
        roles,
        selectedModuleId,
        false
      );
      const orderedActiveUsers = orderRoles(groupedUsers);

      setRoles(orderedActiveUsers);
      setDisplayedRoles(orderedActiveUsers);
      setIsLoading(false);
    })();

  }, []);

  return (
    <Styled.Container>
      <SearchField
        fieldLabel={t(FilterMessages.labelSearchByRoleName)}
        onSearchClick={filterDataOnScreen}
        onClearClick={() => filterDataOnScreen("")}
      />
      <Styled.RolesBoard>
        <Loading loading={isLoading} />
        {displayedRoles.length === 0 && !isLoading ? (
          <Styled.NoRecordsFound>
            <span>{roles.length === 0
              ? t(UserRegisterMessages.noRolesRegistered)
              : t(GlobalMessages.noRecordsFoundText)}
            </span>
          </Styled.NoRecordsFound>
        ) : (
          displayedRoles.map((role) => (
            <RoleCard
              key={role.id}
              currentRole={role}
              showRadioButton
              selectedValue={currentRoleId}
              onClickRadioButton={onRoleChange}
            >
              {role.users?.map((user) => (
                <UserProfile key={user.id} currentUser={user} disableMoreInfo />
              ))}
            </RoleCard>
          ))
        )}
      </Styled.RolesBoard>
    </Styled.Container>
  );
};

GroupSelectionList.defaultProps = {
  currentRoleId: undefined
};

export default GroupSelectionList;